import PageDropZone from '@src/components/common/PageDropZone';
import { STORY_VIDEO_SIZE_GB } from '@src/services/upload/mediaValidation';
import {
  useFolderStructure,
  useUserIdentity,
  useUserStoryUploadFlow,
} from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { useFlagsCombination } from '@src/utility/useFlagsCombination';
import { observer } from 'mobx-react-lite';

import {
  checkDroppedStoryFiles,
  dropZoneHint,
  dropZoneTitle,
} from './uploadCopy';

import type { UploadEntryPoint } from '@src/stores-v2/UserStoryUploadFlowStore';

type Props = {
  /** The page's own add-story gate (button / quick-action flags). */
  enabled: boolean;
  entryPoint: Extract<UploadEntryPoint, 'home-drop' | 'story-manager-drop'>;
  /** Name the folder the story will land in (the Story Manager). */
  showFolder?: boolean;
  /** Runs on Save, like the button that opens the same dialog on this page. */
  onSave?: (context: { storyId: string }) => void;
};

/**
 * Drop a video anywhere on the page to start a story (PLAT-11946, PLAT-11947).
 * The file goes through the Add Stories dialog — the same validation, size
 * limit and analytics as its drop box — which opens on Story Details with the
 * file already uploading.
 */
const StoryPageDropZone = observer(
  ({ enabled, entryPoint, showFolder = false, onSave }: Props) => {
    const uploadFlow = useUserStoryUploadFlow();
    const videoCreator = useVideoCreatorStore();
    const folderStore = useFolderStructure();
    const userIdentity = useUserIdentity();
    const {
      storyManagerAddStoriesEnableButtons,
      enableLargeFileSizeUpload,
      enableNoLimitFileSizeUpload,
    } = useFlagsCombination(userIdentity.currentRole);

    const maxUploadSizeGB = enableNoLimitFileSizeUpload
      ? STORY_VIDEO_SIZE_GB.noLimit
      : enableLargeFileSizeUpload
        ? STORY_VIDEO_SIZE_GB.large
        : STORY_VIDEO_SIZE_GB.default;

    const folderName =
      showFolder && folderStore.currentFolderId
        ? folderStore.foldersById.get(folderStore.currentFolderId)?.name
        : undefined;

    const handleDrop = (files: File[]) => {
      const check = checkDroppedStoryFiles(files);
      if (!check.ok) {
        videoCreator.showToast({ state: 'error', message: check.message });
        return;
      }
      uploadFlow.openNewModal(onSave, {
        initialFiles: [check.file],
        entryPoint,
      });
    };

    return (
      <PageDropZone
        enabled={
          enabled &&
          storyManagerAddStoriesEnableButtons &&
          !uploadFlow.isAnyModalOpen
        }
        onDrop={handleDrop}
        title={dropZoneTitle(folderName)}
        hint={dropZoneHint(maxUploadSizeGB)}
      />
    );
  },
);

export default StoryPageDropZone;
